import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SUPPORTED_LANGUAGES, type Language } from '@/i18n';
import { useLanguage } from '@/i18n/use-language';
import { cn } from '@/lib/utils';

const LABELS: Record<Language, { full: string; short: string }> = {
  en: { full: 'English', short: 'EN' },
  es: { full: 'Español', short: 'ES' },
};

/**
 * Segmented control — used on the signed-out screens, where the choice should be
 * visible rather than buried, and there is room for it.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();

  return (
    <div
      role="group"
      aria-label={t('common.language')}
      className={cn(
        'bg-muted/60 inline-flex items-center gap-0.5 rounded-lg border p-0.5',
        className
      )}
    >
      {SUPPORTED_LANGUAGES.map((code) => {
        const isActive = code === language;
        return (
          <button
            key={code}
            type="button"
            onClick={() => void setLanguage(code)}
            aria-pressed={isActive}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              'focus-visible:ring-ring/50 outline-none focus-visible:ring-2',
              isActive
                ? 'bg-background text-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {LABELS[code].full}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Compact icon menu — for the signed-in header, where the segmented control
 * would crowd the breadcrumb on a narrow screen.
 */
export function LanguageMenu({ className }: { className?: string }) {
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('common.language')}
        className={cn(
          'text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring/50 inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors outline-none focus-visible:ring-2',
          className
        )}
      >
        <Languages className="size-4" />
        {LABELS[language].short}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {SUPPORTED_LANGUAGES.map((code) => (
          <DropdownMenuItem
            key={code}
            onSelect={() => void setLanguage(code)}
            className={cn(code === language && 'font-semibold')}
          >
            {LABELS[code].full}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
